.layout {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

/* Header bar - full width */
.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 60px;
  padding: 0 1.5rem;
  background: rgba(11, 17, 32, 0.92);
  border-bottom: 1px solid var(--border-color);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  /* blur(20px) on a full-width fixed element re-rasterises the entire page
     beneath it on every scroll frame — confirmed frame drops in DevTools.
     blur(8px) is visually indistinguishable at this opacity and ~60% cheaper.
     will-change: transform promotes this element to its own compositor layer
     so the blur is resolved in the GPU compositor pass, not the main thread. */
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  will-change: transform;
}

[data-theme="light"] .header {
  background: rgba(248, 250, 252, 0.92);
  border-bottom: 1px solid var(--border-color);
}

.headerLeft {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

/* Hamburger button - hidden on desktop */
.hamburger {
  display: none;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
  width: 36px;
  height: 36px;
  padding: 6px;
  background: transparent;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s;
}

.hamburger:hover {
  background: var(--btn-hover-bg);
}

.hamburgerLine {
  display: block;
  width: 100%;
  height: 2px;
  background: var(--text-secondary);
  border-radius: 1px;
  transition: background 0.2s;
}

.hamburger:hover .hamburgerLine {
  background: var(--text-primary);
}

.logo {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  font-weight: 700;
  font-size: 1.1rem;
  color: var(--text-primary);
}

[data-theme="light"] .logo {
  color: var(--text-primary);
}

.headerRight {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.userName {
  font-size: 0.875rem;
  color: var(--text-secondary);
}

[data-theme="light"] .userName {
  color: var(--text-secondary);
}

.logoutBtn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: transparent;
  border: 1px solid var(--border-color);
  color: var(--text-secondary);
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
}

.logoutBtn:hover {
  background: var(--btn-hover-bg);
  border-color: rgba(148, 163, 184, 0.25);
  color: var(--text-primary);
}

.logoutBtn svg {
  width: 16px;
  height: 16px;
}

[data-theme="light"] .logoutBtn {
  border: 1px solid var(--border-color);
  color: var(--text-secondary);
}

[data-theme="light"] .logoutBtn:hover {
  background: var(--btn-hover-bg);
  border-color: rgba(15, 23, 42, 0.15);
  color: var(--text-primary);
}

/* Container for sidebar + main */
.container {
  display: flex;
  flex: 1;
  padding-top: 60px;
}

/* Sidebar */
.sidebar {
  width: 220px;
  background: rgba(17, 24, 39, 0.5);
  border-right: 1px solid var(--border-color);
  padding: 1rem 0;
  position: fixed;
  top: 60px;
  left: 0;
  bottom: 0;
  overflow-y: auto;
  transition: width 0.2s ease, transform 0.25s ease;
  z-index: 90;
  display: flex;
  flex-direction: column;
}

[data-theme="light"] .sidebar {
  background: var(--bg-secondary);
  border-right: 1px solid var(--border-color);
}

.sidebarNav {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0 0.75rem;
}

.navItem {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.7rem 1rem;
  color: var(--text-secondary);
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 500;
  border-radius: 8px;
  transition: all 0.2s;
}

.navItem:hover {
  background: var(--btn-hover-bg);
  color: var(--text-primary);
}

.navItem.active {
  background: rgba(34, 211, 238, 0.08);
  color: var(--text-accent);
}

.navItem.active .navIcon {
  color: var(--text-accent);
}

[data-theme="light"] .navItem {
  color: var(--text-secondary);
}

[data-theme="light"] .navItem:hover {
  background: var(--btn-hover-bg);
  color: var(--text-primary);
}

[data-theme="light"] .navItem.active {
  background: rgba(8, 145, 178, 0.08);
  color: var(--text-accent);
}

[data-theme="light"] .navItem.active .navIcon {
  color: var(--text-accent);
}

.navIcon {
  width: 18px;
  height: 18px;
  opacity: 0.7;
  transition: all 0.2s;
  flex-shrink: 0;
}

.navItem.active .navIcon {
  opacity: 1;
}

/* Extension links at bottom of sidebar */
.extensionLinks {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1.25rem 0.75rem;
  margin-top: auto;
}

.extensionLink {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  color: var(--text-secondary);
  text-decoration: none;
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.35rem 0.5rem;
  border-radius: 6px;
  transition: all 0.2s;
  opacity: 0.85;
}

.extensionLink:hover {
  opacity: 1;
  color: var(--text-primary);
  background: var(--btn-hover-bg);
}

.extensionIcon {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
}

/* Plan badge at bottom of sidebar */
.planBadge {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  margin: 0.75rem 0.75rem 0;
  border-top: 1px solid var(--border-color);
  padding-top: 1rem;
  min-width: 0;
}

.planRow {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
}

.planDot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.planLabel {
  font-size: 0.8rem;
  font-weight: 500;
  color: var(--text-secondary);
}

.betaTag {
  font-size: 0.6rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding: 0.1rem 0.4rem;
  border-radius: 9999px;
  background: var(--color-warning-light);
  color: var(--color-warning);
  border: 1px solid var(--color-warning-border);
  line-height: 1;
}

/* Trial badge — single consolidated element */
.trialBadge {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  text-decoration: none;
  padding: 0.3rem 0.6rem;
  border-radius: 8px;
  background: linear-gradient(135deg, rgba(168, 85, 247, 0.08), rgba(34, 211, 238, 0.06));
  border: 1px solid rgba(168, 85, 247, 0.15);
  transition: all 0.25s ease;
  cursor: pointer;
  white-space: nowrap;
}

.trialBadge:hover {
  background: linear-gradient(135deg, rgba(168, 85, 247, 0.14), rgba(34, 211, 238, 0.10));
  border-color: rgba(168, 85, 247, 0.3);
}

.trialDot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #a855f7;
  box-shadow: 0 0 6px rgba(168, 85, 247, 0.5);
  flex-shrink: 0;
}

.trialLabel {
  font-size: 0.75rem;
  font-weight: 600;
  color: #c4b5fd;
  letter-spacing: 0.01em;
}

.trialSep {
  color: rgba(148, 163, 184, 0.3);
  font-size: 0.7rem;
}

.trialCountdown {
  font-size: 0.7rem;
  font-weight: 500;
  color: var(--text-accent);
}

[data-theme="light"] .trialBadge {
  background: linear-gradient(135deg, rgba(168, 85, 247, 0.06), rgba(8, 145, 178, 0.04));
  border-color: rgba(168, 85, 247, 0.12);
}

[data-theme="light"] .trialBadge:hover {
  background: linear-gradient(135deg, rgba(168, 85, 247, 0.10), rgba(8, 145, 178, 0.07));
  border-color: rgba(168, 85, 247, 0.22);
}

[data-theme="light"] .trialLabel {
  color: #7c3aed;
}

[data-theme="light"] .trialCountdown {
  color: #0891b2;
}

.upgradeLink {
  display: block;
  width: 100%;
  text-align: center;
  font-size: 0.7rem;
  font-weight: 600;
  color: var(--text-accent);
  text-decoration: none;
  padding: 0.4rem 0.625rem;
  border: 1px solid rgba(34, 211, 238, 0.25);
  border-radius: 6px;
  background: rgba(34, 211, 238, 0.06);
  transition: all 0.2s;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
}

.trialCta {
  display: block;
  width: 100%;
  font-family: inherit;
  font-size: 0.72rem;
  font-weight: 600;
  color: #c4b5fd;
  text-align: center;
  padding: 0.4rem 0.5rem;
  border-radius: 8px;
  border: 1px solid rgba(168, 85, 247, 0.25);
  background: linear-gradient(135deg, rgba(168, 85, 247, 0.10), rgba(34, 211, 238, 0.08));
  cursor: pointer;
  transition: all 0.2s;
  letter-spacing: 0.02em;
  white-space: nowrap;
}

.trialCta:hover:not(:disabled) {
  background: linear-gradient(135deg, rgba(168, 85, 247, 0.18), rgba(34, 211, 238, 0.14));
  border-color: rgba(168, 85, 247, 0.4);
}

.trialCta:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

[data-theme="light"] .trialCta {
  color: #7c3aed;
  border-color: rgba(168, 85, 247, 0.2);
  background: linear-gradient(135deg, rgba(168, 85, 247, 0.08), rgba(8, 145, 178, 0.05));
}

[data-theme="light"] .trialCta:hover:not(:disabled) {
  background: linear-gradient(135deg, rgba(168, 85, 247, 0.14), rgba(8, 145, 178, 0.10));
  border-color: rgba(168, 85, 247, 0.32);
}

.trialError {
  font-size: 0.65rem;
  color: var(--color-danger, #f87171);
  line-height: 1.3;
}

.upgradeLink:hover {
  background: rgba(34, 211, 238, 0.12);
  border-color: rgba(34, 211, 238, 0.4);
}

[data-theme="light"] .upgradeLink {
  border-color: rgba(8, 145, 178, 0.2);
  background: rgba(8, 145, 178, 0.06);
}

[data-theme="light"] .upgradeLink:hover {
  background: rgba(8, 145, 178, 0.1);
  border-color: rgba(8, 145, 178, 0.3);
}

/* Main content */
.main {
  flex: 1;
  margin-left: 220px;
  padding: 1.5rem 2rem;
  min-height: calc(100vh - 60px);
  transition: margin-left 0.2s ease;
  overflow-x: clip;
  min-width: 0;
  position: relative;
}

/* Sidebar overlay backdrop (mobile only) */
.sidebarOverlay {
  display: none;
}

/* ─── Tablet: icon-only sidebar ─── */
@media (max-width: 1024px) {
  .sidebar {
    width: 60px;
    padding: 0.75rem 0;
  }

  .navItem span {
    display: none;
  }

  .navItem {
    justify-content: center;
    padding: 0.75rem;
  }

  .main {
    margin-left: 60px;
    padding: 1.5rem;
  }

  .planBadge {
    justify-content: center;
    padding: 0.75rem 0;
    margin: 0.75rem 0.5rem 0;
  }

  .planLabel,
  .betaTag,
  .trialBadge,
  .upgradeLink,
  .trialCta,
  .trialError {
    display: none;
  }

  .extensionLinks {
    flex-direction: column;
    align-items: center;
    padding: 0.5rem 0 0.75rem;
    gap: 0.25rem;
  }

  .extensionLink span {
    display: none;
  }

  .extensionLink {
    padding: 0.35rem;
  }

  .userName {
    display: none;
  }

  .logoutBtn span {
    display: none;
  }

  .logoutBtn {
    padding: 0.5rem;
  }
}

/* ─── Mobile: sidebar becomes overlay ─── */
@media (max-width: 768px) {
  .hamburger {
    display: flex;
  }

  .sidebar {
    width: 260px;
    transform: translateX(-100%);
    box-shadow: none;
    background: rgba(17, 24, 39, 0.98);
  }

  [data-theme="light"] .sidebar {
    background: rgba(248, 250, 252, 0.98);
  }

  .sidebar.sidebarOpen {
    transform: translateX(0);
    box-shadow: 4px 0 24px rgba(0, 0, 0, 0.3);
  }

  /* Restore full nav text in overlay mode */
  .sidebar.sidebarOpen .navItem span {
    display: inline;
  }

  .sidebar.sidebarOpen .navItem {
    justify-content: flex-start;
    padding: 0.7rem 1rem;
  }

  .sidebar.sidebarOpen .planBadge {
    justify-content: flex-start;
    padding: 0.75rem 1.25rem;
  }

  .sidebar.sidebarOpen .planLabel,
  .sidebar.sidebarOpen .betaTag,
  .sidebar.sidebarOpen .trialBadge,
  .sidebar.sidebarOpen .upgradeLink {
    display: flex;
  }

  .sidebar.sidebarOpen .trialCta {
    display: block;
  }

  .sidebar.sidebarOpen .trialError {
    display: block;
  }

  .sidebar.sidebarOpen .extensionLink span {
    display: inline;
  }

  .sidebar.sidebarOpen .extensionLinks {
    justify-content: flex-start;
    padding: 0.5rem 1.25rem 0.75rem;
  }

  .sidebarOverlay {
    display: block;
    position: fixed;
    inset: 0;
    top: 60px;
    background: rgba(0, 0, 0, 0.5);
    z-index: 80;
  }

  .main {
    margin-left: 0;
    padding: 1rem;
  }

  .header {
    padding: 0 1rem;
  }

  .headerRight {
    gap: 0.5rem;
  }
}

/* ─── Small phones ─── */
@media (max-width: 400px) {
  .logo span {
    display: none;
  }

  .main {
    padding: 0.75rem;
  }
}
